import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ZodError, ZodIssue } from "zod";

/**
 * The type of error that can occur in the payments API client.
 * @enum {string}
 * @readonly
 */
export enum ErrorCodes {
    /**
     * The error code for zod validation errors.
     * @type {string}
     */
    VALIDATION_ERROR = "VALIDATION_ERROR",
    /**
     * The error code for Halliday Payments API business logic errors.
     * @type {string}
     */
    API_ERROR = "API_ERROR",
    /**
     * The error code for server 500 errors.
     * @type {string}
     */
    SERVER_ERROR = "SERVER_ERROR",
    /**
     * The error code for axios/network errors.
     * @type {string}
     */
    NETWORK_ERROR = "NETWORK_ERROR",
    /**
     * The error code for unknown errors.
     * @type {string}
     */
    UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Custom error class for the payments API client.
 * @extends Error
 * @property {string} name - HallidayPaymentsApiClientError.
 * @property {ErrorCodes} code - The error code.
 * @property {string} message - The message of the error.
 * @property {string} validationMessage - The message from zod validation error.
 * @property {ZodError["issues"]} validationIssues - The issues from zod validation error.
 * @property {number} status - The HTTP request status.
 * @property {string} statusText - The HTTP request status text.
 * @property {Record<string, any>} reqHeaders - The HTTP request headers.
 * @property {Record<string, any>} resHeaders - The HTTP response headers.
 * @property {any} data - The response data from the server.
 * @property {AxiosRequestConfig} config - The request configuration.
 * @property {any} request - The request object.
 * @property {AxiosResponse} response - The response object.
 * @property {Error} error - The error object.
 */
export class PaymentsApiClientError extends Error {
    override name: string = "HallidayPaymentsApiClientError";
    code: ErrorCodes;
    override message: string;

    // Zod Validation Errors
    validationIssues: ZodError["issues"] | null = null;
    validationMessage: ZodError["message"] | string | null = null;

    // Axios provided properties
    status: number | null = null;
    statusText: string | null = null;
    reqHeaders: Record<string, any> | null = null;
    resHeaders: Record<string, any> | null = null;
    data: any | null = null;
    config: AxiosRequestConfig | null = null;
    request: any | null = null;
    response: AxiosResponse | null = null;

    // Fallback error
    error: Error | any = null;

    constructor({
        message,
        code,
        status,
        statusText,
        reqHeaders,
        resHeaders,
        data,
        config,
        request,
        response,
        error,
        validationIssues,
        validationMessage,
    }: {
        message: string;
        code: ErrorCodes;
        status?: number;
        statusText?: string;
        reqHeaders?: Record<string, any>;
        resHeaders?: Record<string, any>;
        data?: any;
        config?: AxiosRequestConfig;
        request?: any;
        response?: AxiosResponse;
        error?: Error;
        validationIssues?: ZodError["issues"] | null;
        validationMessage?: ZodError["message"] | string | null;
    }) {
        super(message);
        this.code = code;
        this.message = message;
        this.status = status ?? null;
        this.statusText = statusText ?? null;
        this.reqHeaders = reqHeaders ?? null;
        this.resHeaders = resHeaders ?? null;
        this.data = data ?? null;
        this.config = config ?? null;
        this.request = request ?? null;
        this.response = response ?? null;
        this.error = error ?? null;
        this.validationIssues = validationIssues ?? null;
        this.validationMessage = validationMessage ?? null;

        // Maintains proper prototype chain
        Object.setPrototypeOf(this, PaymentsApiClientError.prototype);
    }

    static isPaymentsApiClientError(error: unknown): error is PaymentsApiClientError {
        return error instanceof PaymentsApiClientError;
    }

    static parseValidationError({
        message,
        error,
    }: {
        message?: string;
        error?: ZodError | Error;
    }): PaymentsApiClientError {
        const validationMessage = message ?? error?.message ?? "Validation error";
        return new PaymentsApiClientError({
            message: validationMessage,
            code: ErrorCodes.VALIDATION_ERROR,
            validationIssues: error instanceof ZodError ? error.issues : null,
            validationMessage: error instanceof ZodError ? error.message : null,
            ...(error !== undefined && { error }),
        });
    }

    static parseAxiosError(error: AxiosError): PaymentsApiClientError | null {
        if (error.response) {
            const errorStatus = error.response.status;
            let errorsFromResponse: string | string[] | ZodIssue[] = (error.response.data as any)?.errors || [];
            let validationIssues: ZodIssue[] | null = null;
            if (!Array.isArray(errorsFromResponse)) {
                errorsFromResponse = [errorsFromResponse];
            }

            let errorMessages =
                errorsFromResponse
                    .map((errorFromResponse: string | object) => {
                        if (typeof errorFromResponse !== "string") {
                            return JSON.stringify(errorFromResponse);
                        }
                        return errorFromResponse;
                    })
                    .join("\n") ||
                error.message ||
                "Unknown error";
            let statusText = error.response?.statusText;

            if (errorStatus === 500) {
                return new PaymentsApiClientError({
                    message: errorMessages,
                    code: ErrorCodes.SERVER_ERROR,
                    status: 500,
                    statusText: `${error.code ?? "500"}: ${statusText ?? "Server Error"}`,
                    data: error.response?.data,
                    ...(error.config !== undefined && { config: error.config }),
                    request: error.request,
                    response: error.response,
                    reqHeaders: error.request?.headers,
                    resHeaders: error.response?.headers,
                });
            } else if (errorStatus === 400) {
                errorMessages = `Bad request: ${errorMessages}`;
                statusText = `${error.code ?? "400"}: ${statusText ?? "Bad Request"}`;
                validationIssues = errorsFromResponse as ZodIssue[];
            } else if (errorStatus === 401) {
                errorMessages = `Unauthorized: ${errorMessages}`;
                statusText = `${error.code ?? "401"}: ${statusText ?? "Unauthorized"}`;
            } else if (errorStatus === 403) {
                errorMessages = `Forbidden: ${errorMessages}`;
                statusText = `${error.code ?? "403"}: ${statusText ?? "Forbidden"}`;
            } else if (errorStatus === 404) {
                errorMessages = `Resource not found: ${errorMessages}`;
                statusText = `${error.code ?? "404"}: ${statusText ?? "Not Found"}`;
            } else {
                errorMessages = `Request failed with status ${errorStatus}: ${errorMessages}`;
                statusText = `${error.code ?? errorStatus}: ${statusText ?? "Unexpected error"}`;
            }
            return new PaymentsApiClientError({
                message: errorMessages,
                code: ErrorCodes.API_ERROR,
                status: errorStatus,
                statusText,
                data: error.response?.data,
                ...(error.config !== undefined && { config: error.config }),
                request: error.request,
                response: error.response,
                reqHeaders: error.request?.headers,
                resHeaders: error.response?.headers,
                validationIssues,
            });
        } else if (error.request) {
            return new PaymentsApiClientError({
                message: error.message || `No response received from the server: ${error.code}`,
                code: ErrorCodes.NETWORK_ERROR,
                ...(error.config !== undefined && { config: error.config }),
                request: error.request,
                reqHeaders: error.request?.headers,
            });
        } else {
            // Aborted requests are not errors, handle it gracefully
            if (error.code === "ERR_CANCELED") {
                return null;
            }
            return new PaymentsApiClientError({
                message: `${error.code ? `${error.code}: ` : ""}${error.message || "No message"}`,
                code: ErrorCodes.NETWORK_ERROR,
                ...(error.config !== undefined && { config: error.config }),
                request: error.request,
                error,
            });
        }
    }

    toJSON(): string {
        return JSON.stringify({
            name: this.name,
            code: this.code,
            message: this.message,
            validationIssues: this.validationIssues,
            status: this.status,
            statusText: this.statusText,
            config: this.config,
            stack: this.stack,
        });
    }
}

/**
 * Creates a response error interceptor for axios.
 */
export const createErrorInterceptor = () => {
    return {
        onFulfilled: <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
            // Pass through the response
            return response;
        },
        onRejected: (error: any) => {
            if (axios.isAxiosError(error)) {
                const newError = PaymentsApiClientError.parseAxiosError(error);
                if (newError) throw newError;
                return { data: null };
            } else {
                throw new PaymentsApiClientError({
                    message: error.message || `Unknown error`,
                    code: ErrorCodes.UNKNOWN_ERROR,
                    error,
                });
            }
        },
    };
};
